<template>
  <ReportCellWrapper
    :trial-over="params.data?.trialOver"
    :is-disabled-by-tier="params.data?.isDisabledByTier"
    class="max-w-full"
  >
    <component
      :is="reportNameToCellInfo[reportType]?.tooltip ? BittsTooltip : 'div'"
      :mount-to-body="true"
      placement="bottom"
      data-testid="report-partner-overlaps-cell"
      overlay-class="report-partner-overlaps-cell__partner-tooltip"
      class="flex items-center gap-4 min-w-0"
    >
      <FontAwesomeIcon
        v-if="reportNameToCellInfo[reportType]?.icon"
        :icon="reportNameToCellInfo[reportType]?.icon || []"
        class="text-neutral-accent h-14 w-14 shrink-0"
      />
      <span class="truncate min-w-0">
        {{ reportNameToCellInfo[reportType]?.text }}</span
      >
      <BittsTooltip
        v-if="isPartnerTagsType && overflowPartnerTags.length"
        :mount-to-body="true"
        overlay-class="report-partner-overlaps-cell__partner-tooltip"
      >
        <BittsTag
          size="x-small"
          color="neutral"
          class="report-partner-overlaps-cell__overflow-tag shrink-0"
        >
          +{{ overflowPartnerTags.length }}
        </BittsTag>
        <template #title>
          <div class="flex flex-col gap-4">
            <span v-for="tag in overflowPartnerTags" :key="String(tag.id)">{{
              tag.label
            }}</span>
          </div>
        </template>
      </BittsTooltip>
      <template #title>
        <div
          v-if="reportNameToCellInfo[reportType]?.tooltip"
          class="report-partner-overlaps-cell__partner-list flex flex-col gap-8 max-h-[300px] overflow-y-auto"
        >
          <BittsAvatarItem
            v-for="partner in params.data?.partnersInReport"
            :key="partner.id"
            :org="partner"
            :show-initials="true"
            size="small"
            spacing="condensed"
            :title="partner.name"
            class="report-partner-overlaps-cell__partner-avatar"
            :has-dark-bg="true"
          />
        </div>
      </template>
    </component>
  </ReportCellWrapper>
</template>

<script setup lang="ts">
import { BittsAvatarItem, BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';

import ReportCellWrapper from '@/components/reports/ReportCellWrapper.vue';

import {
  MULTI_PARTNER_REPORT_TYPES,
  REPORT_TYPE_NAMES,
} from '@/constants/reports';
import { usePartnersStore } from '@/stores';
import { PartnerTag } from '@/types/partners';
import { ReportCell, ReportTypeName } from '@/types/reports';

const { params } = defineProps<{
  params: ICellRendererParams<ReportCell>;
}>();

const reportType = computed<ReportTypeName>(() => {
  if (params.data?.isFolder) return 'single-partner-overlaps';
  return params.data?.reportType || 'single-partner-overlaps';
});

const partnersStore = usePartnersStore();

const isPartnerTagsType = computed(
  () =>
    reportType.value === REPORT_TYPE_NAMES.PARTNER_TAGS_TYPE &&
    !params.data?.isStaticList &&
    !params.data?.isFolder,
);

const partnerTagsInReport = computed<PartnerTag[]>(() => {
  const data = params.data;
  if (!data || data.isStaticList || data.isFolder) return [];
  const tagIds = data.report.tag_ids?.length
    ? data.report.tag_ids
    : data.report.tag_id
      ? [data.report.tag_id]
      : [];
  return tagIds
    .map((id: string) => partnersStore.getPartnerTagById(id))
    .filter((tag): tag is PartnerTag => !!tag);
});

const overflowPartnerTags = computed(() => partnerTagsInReport.value.slice(1));

function getMultiplePartnerText(): string {
  if (params.data?.isStaticList || params.data?.isFolder) {
    return '--';
  }
  if (
    !MULTI_PARTNER_REPORT_TYPES.includes(reportType.value || '') ||
    !params.data?.partnersInReport.length
  ) {
    return '--';
  }
  if (params.data.partnersInReport.length === 1) {
    return params.data.partnersInReport[0]?.name || '--';
  }
  return `${params.data.partnersInReport.length} Partners`;
}
function getSinglePartnerText(): string {
  if (MULTI_PARTNER_REPORT_TYPES.includes(reportType.value || '')) {
    return '--';
  }
  return params.data?.partnersInReport[0]?.name || '--';
}
const reportNameToCellInfo = computed<
  Record<
    ReportTypeName,
    {
      text: string;
      icon?: string[];
      tooltip?: boolean;
    }
  >
>(() => {
  return {
    [REPORT_TYPE_NAMES.ALL_PARTNERS_TYPE]: {
      text: 'All Partners',
      icon: ['fak', 'globe'],
    },
    [REPORT_TYPE_NAMES.CUSTOM_TYPE]: {
      text: getMultiplePartnerText(),
      tooltip: !!params.data?.partnersInReport.length,
    },
    [REPORT_TYPE_NAMES.GREENFIELD_TYPE]: { text: getSinglePartnerText() },
    [REPORT_TYPE_NAMES.PARTNER_TAGS_TYPE]: {
      text: partnerTagsInReport.value[0]?.label || '--',
      icon: ['fak', 'tag'],
    },
    [REPORT_TYPE_NAMES.POTENTIAL_REVENUE_TYPE]: {
      text: getSinglePartnerText(),
    },
    [REPORT_TYPE_NAMES.SINGLE_OVERLAPS_TYPE]: { text: getSinglePartnerText() },
  };
});
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.report-partner-overlaps-cell__partner-avatar {
  .bitts-avatar-item__text-title {
    font-weight: normal !important;
  }
}

.report-partner-overlaps-cell__partner-list {
  ::-webkit-scrollbar-track {
    @apply bg-neutral-700;
  }

  ::-webkit-scrollbar-thumb {
    @apply bg-neutral-700 rounded border-2 border-neutral-700;
  }

  ::-webkit-scrollbar-thumb:hover {
    @apply bg-neutral-800;
  }

  scrollbar-width: thin;
  scrollbar-color: theme('colors.neutral-800')
    theme('colors.neutral-background-tooltip');
}
</style>
